import {
  Modal,
  Text,
  Timeline,
  ThemeIcon,
  Loader,
  Center,
  Pagination,
} from "@mantine/core";
import { IconCoin, IconArrowRight, IconGift } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Endpoints } from "../../Lib/Endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { LoyaltyTransactionProps } from "../../Lib/Types";
import { Colors } from "../../Lib/Data";
import dayjs from "dayjs";

interface LoyaltyHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  theme: "dark" | "light";
}

const LoyaltyHistoryModal: React.FC<LoyaltyHistoryModalProps> = ({
  opened,
  onClose,
  theme,
}) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<LoyaltyTransactionProps[]>(
    []
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchHistory = async () => {
    setLoading(true);
    const r = await PerformRequest({
      method: "GET",
      route: Endpoints.GetLoyaltyHistory(page, limit),
      callback: () => setLoading(false),
    });

    if (r && r.status === 200) {
      setTransactions((r.data as any).transactions);
      setTotalPages((r.data as any).totalPages);
    }
  };

  useEffect(() => {
    if (opened) {
      fetchHistory();
    }
  }, [opened, page]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Loyalty History"
      centered
      radius="md"
      size="lg"
      styles={{
        header: {
          backgroundColor: theme === "dark" ? "#1A1B1E" : "#fff",
          color: theme === "dark" ? "#fff" : "#000",
        },
        body: {
          backgroundColor: theme === "dark" ? "#1A1B1E" : "#fff",
        },
        title: {
          fontWeight: 700,
        },
      }}
    >
      {loading ? (
        <Center h={200}>
          <Loader color={Colors.GreenPrimary} />
        </Center>
      ) : transactions.length === 0 ? (
        <Center h={150}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Text c="dimmed">No loyalty transactions yet.</Text>
          </div>
        </Center>
      ) : (
        <div style={{ padding: "10px 0" }}>
          <Timeline active={transactions.length} bulletSize={24} lineWidth={2}>
            {transactions.map((tx) => (
              <Timeline.Item
                key={tx.id}
                bullet={
                  <ThemeIcon
                    size={22}
                    radius="xl"
                    color={
                      tx.type === "earned"
                        ? Colors.GreenPrimary
                        : Colors.OrangePrimary
                    }
                  >
                    {tx.type === "earned" ? (
                      <IconCoin size={12} />
                    ) : (
                      <IconGift size={12} />
                    )}
                  </ThemeIcon>
                }
                title={
                  <Text
                    fw={500}
                    size="sm"
                    c={theme === "dark" ? "white" : "dark"}
                  >
                    {tx.type === "earned" ? "Points Earned" : "Points Redeemed"}
                  </Text>
                }
              >
                <Text c="dimmed" size="xs" mt={4}>
                  {dayjs(tx.createdAt).format("MMM D, YYYY h:mm A")}
                </Text>
                <Text size="sm" mt={4} style={{ lineHeight: 1.4 }}>
                  {tx.description}
                </Text>
                <Text
                  size="sm"
                  fw={700}
                  mt={4}
                  c={
                    tx.type === "earned"
                      ? Colors.GreenSecondary
                      : Colors.OrangeSecondary
                  }
                >
                  {tx.type === "earned" ? "+" : "-"}
                  {tx.amount} Points
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>

          {totalPages > 1 && (
            <Center mt={20}>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                color="green"
              />
            </Center>
          )}
        </div>
      )}
    </Modal>
  );
};

export default LoyaltyHistoryModal;
